import { Collapse, ListItemButton, ListItemIcon, ListItemText, Tooltip, Box, styled } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  '&.active .MuiListItemButton-root': {
    backgroundColor: theme.palette.primary.lighter,
    '&:hover': {
      backgroundColor: theme.palette.primary.lighter,
    }
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  my: 0.5,
  transition: theme.transitions.create(['background-color', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateX(4px)'
  },
  '&.active': {
    backgroundColor: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main
    }
  },
}));

export const MenuItem = ({ item, isOpen, onToggle, isCollapsed, handleDrawerToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const hasSubItems = item.subItems && item.subItems.length > 0;
  
  const isActive = item.path 
    ? (item.exact 
        ? location.pathname === item.path 
        : location.pathname.startsWith(item.path))
    : false;
  
  const isChildActive = hasSubItems && 
    item.subItems.some(subItem => 
      location.pathname.startsWith(subItem.path)
    );

  const handleClick = () => {
    if (hasSubItems) {
      onToggle(item.text);
    } else if (handleDrawerToggle) {
      handleDrawerToggle();
    }
  };

  const listItemContent = (
    <>
      <ListItemIcon sx={{ 
        minWidth: isCollapsed ? '40px' : '36px',
        color: isActive || isChildActive ? theme.palette.primary.main : 'inherit',
        transition: 'color 0.2s ease'
      }}>
        {item.icon}
      </ListItemIcon>
      {!isCollapsed && (
        <ListItemText 
          primary={item.text} 
          primaryTypographyProps={{ 
            fontWeight: isActive || isChildActive ? 600 : 500,
            variant: 'body2',
            noWrap: true
          }} 
        />
      )}
      {hasSubItems && !isCollapsed && (
        <Box sx={{ 
          ml: 1,
          display: 'flex',
          alignItems: 'center',
          color: isActive || isChildActive ? theme.palette.primary.main : 'inherit'
        }}>
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </Box>
      )}
    </>
  );

  return (
    <>
      {isCollapsed ? (
        <Tooltip title={item.text} placement="right" arrow>
          <Box>
            <StyledNavLink 
              to={hasSubItems ? '#' : item.path}
              activeClassName="active"
              onClick={handleClick}
            >
              <StyledListItemButton
                component={hasSubItems ? 'div' : NavLink}
                sx={{
                  justifyContent: 'center',
                  minHeight: '48px',
                  px: 2,
                  '&:hover': {
                    transform: 'none'
                  }
                }}
              >
                {listItemContent}
              </StyledListItemButton>
            </StyledNavLink>
          </Box>
        </Tooltip>
      ) : (
        <StyledNavLink 
          to={hasSubItems ? '#' : item.path}
          activeClassName="active"
        >
          <StyledListItemButton 
            component={hasSubItems ? 'div' : NavLink}
            onClick={handleClick}
            sx={{
              pl: 2.5,
              pr: 1.5,
            }}
          >
            {listItemContent}
          </StyledListItemButton>
        </StyledNavLink>
      )}
      
      {hasSubItems && !isCollapsed && (
        <Collapse 
          in={isOpen} 
          timeout="auto" 
          unmountOnExit
          sx={{
            ml: 1,
            borderLeft: `1px solid ${theme.palette.divider}`,
            pl: 1
          }}
        >
          {item.subItems.map((subItem) => {
            const isSubItemActive = location.pathname.startsWith(subItem.path);
            return (
              <StyledNavLink 
                key={subItem.text}
                to={subItem.path}
                activeClassName="active"
                onClick={handleDrawerToggle}
              >
                <StyledListItemButton
                  sx={{
                    pl: 3,
                    '&.active': {
                      backgroundColor: theme.palette.primary.lighter,
                      color: theme.palette.primary.main,
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: '36px',
                    color: isSubItemActive 
                      ? theme.palette.primary.main 
                      : 'inherit'
                  }}>
                    {subItem.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={subItem.text} 
                    primaryTypographyProps={{ 
                      fontWeight: isSubItemActive ? 600 : 400,
                      variant: 'body2',
                      noWrap: true
                    }} 
                  />
                </StyledListItemButton>
              </StyledNavLink>
            );
          })}
        </Collapse>
      )}
    </>
  );
};